import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import WellwiredInquiry from "../components/WellwiredInquiry";
import WellwiredDisclosure from "../components/WellwiredDisclosure";
import WellwiredClock from "../components/WellwiredClock";
import useWellwiredScroll from "../hooks/useWellwiredScroll";
import { RoomStudy, PillarStudy } from "../components/WellwiredVisuals";
import { blueprintRate, consultPrice, fitCallUrl } from "../config/wellwired";
import "./WellwiredTokens.css";
import "./Wellwired.css";

// Dawn uses the first 15% of the pinned travel; later scenes each get 21.25%.
const moments = [
  {
    name: "Dawn",
    time: "06:30",
    minutes: 390,
    scrollStart: 0,
    title: "The house welcomes the day.",
    copy: "The daytime lighting scene begins on schedule. Ordinary switches remain ready whenever you want something different.",
  },
  {
    name: "Midday",
    time: "12:00",
    minutes: 720,
    scrollStart: 0.15,
    title: "Air quality gets attention all day.",
    copy: "CO₂, particles, VOC trends and humidity are monitored. Ventilation responds within the mechanical plan, including the home’s baseline fresh-air requirements.",
  },
  {
    name: "Dusk",
    time: "18:30",
    minutes: 1110,
    scrollStart: 0.3625,
    title: "A softer light to come home to.",
    copy: "As the day changes, the lighting changes with it. Warm, low light takes the place of daytime brightness.",
  },
  {
    name: "Evening",
    time: "22:00",
    minutes: 1320,
    scrollStart: 0.575,
    title: "Selected Wi-Fi access points turn off.",
    copy: "The wireless schedule follows your household’s priorities. Hardwired connections keep air monitoring, ventilation and leak protection operating.",
  },
  {
    name: "Night",
    time: "02:00",
    minutes: 1560,
    scrollStart: 0.7875,
    title: "A little light. Just where you need it.",
    copy: "A low path light guides the way when you get up, then the room returns to darkness. Air monitoring and hardwired leak protection stay active.",
  },
];
const pillars = [
  [
    "Light",
    "Light that follows the day.",
    "Daytime settings, warm evening scenes and gentle nighttime light, coordinated with your lighting designer.",
    "We plan schedules, dimming and transitions with your lighting designer and electrical team. Ordinary switches remain available whenever you want something different.",
  ],
  [
    "Signal",
    "Low-voltage systems. Low-RF connections.",
    "Low-voltage hardware and controls, local networking and planned Wi-Fi, designed together to reduce unnecessary RF and EMF exposure.",
    "We plan the hardware, power supplies, switching, wiring routes and equipment placement with your electrical team and, where involved, your building biologist. Hardwired controls and local network connections come first, with selected Wi-Fi access points turning off on schedule. The design considers electric and magnetic fields alongside RF. Any measurements to check agreed targets are defined in the optional Verify scope.",
    "Electrical",
  ],
  [
    "Air",
    "Indoor air quality, measured all day.",
    "Monitoring CO₂, particles, VOC trends and humidity, with ventilation coordinated within the mechanical plan.",
    "The Blueprint defines sensor locations and ventilation responses while preserving baseline fresh-air requirements. Sensors measure their specified indicators; source control, ventilation and filtration each have a role in the wider air-quality plan.",
  ],
  [
    "Water",
    "Catch leaks early. Limit the damage.",
    "Hardwired leak sensing and automatic water shutoff help limit further water release from detected leaks.",
    "We plan sensor locations, local valve control, alerts and filtration monitoring with the plumbing team. The response keeps working when selected Wi-Fi is off. Detected leaks still need repair, and wet materials need prompt drying.",
  ],
  [
    "Quiet",
    "A home that asks less of you.",
    "Ordinary switches, centralized equipment and local controls. Screens and phone controls are optional.",
    "We plan fewer competing interfaces and keep everyday operation local. The technology belongs in the service spaces and drawings, with familiar controls in the rooms you live in.",
    "Peace",
  ],
];
export default function Wellwired() {
  const dayRef = useRef(null);
  const headerRef = useRef(null);
  const menuButtonRef = useRef(null);
  const selectedMomentRef = useRef(null);
  const refreshDayRef = useRef(null);
  const { scrollTo, cancel: cancelScroll } = useWellwiredScroll();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [moment, setMoment] = useState(0);
  const [staticDay, setStaticDay] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const closeOutside = (event) => {
      if (!headerRef.current?.contains(event.target)) setMenuOpen(false);
    };
    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [menuOpen]);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const short = window.matchMedia("(max-height: 700px)");
    const narrow = window.matchMedia("(max-width: 760px)");
    let frame = 0;
    const update = () => {
      frame = 0;
      setIsScrolled(window.scrollY > 0);
      const useStatic = reduced.matches || short.matches || narrow.matches;
      setStaticDay(useStatic);
      if (useStatic || selectedMomentRef.current !== null || !dayRef.current) return;
      const rect = dayRef.current.getBoundingClientRect();
      const headerHeight = headerRef.current?.offsetHeight || 0;
      const travel = rect.height - window.innerHeight + headerHeight;
      const progress = Math.max(0, Math.min(1, (headerHeight - rect.top) / travel));
      // Discrete triggers; a small reverse buffer prevents trackpad jitter
      // around a boundary from repeatedly restarting the timed transition.
      const next = moments.reduce((selected, item, index) => progress >= item.scrollStart ? index : selected, 0);
      setMoment((previous) => {
        if (next >= previous || progress < moments[previous].scrollStart - 0.02) return next;
        return previous;
      });
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    refreshDayRef.current = schedule;
    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    reduced.addEventListener("change", schedule);
    short.addEventListener("change", schedule);
    narrow.addEventListener("change", schedule);
    return () => {
      refreshDayRef.current = null;
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      reduced.removeEventListener("change", schedule);
      short.removeEventListener("change", schedule);
      narrow.removeEventListener("change", schedule);
    };
  }, []);

  const resumeDay = () => {
    selectedMomentRef.current = null;
    refreshDayRef.current?.();
  };

  const selectMoment = (index) => {
    cancelScroll();
    setMoment(index);
    if (staticDay) {
      return;
    }
    const element = dayRef.current;
    const top = window.scrollY + element.getBoundingClientRect().top;
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const travel = element.offsetHeight - window.innerHeight + headerHeight;
    // Keep the requested scene selected while crossing intermediate scroll cues.
    selectedMomentRef.current = index;
    scrollTo(top - headerHeight + travel * (moments[index].scrollStart + 0.01), {
      onComplete: resumeDay,
      onCancel: resumeDay,
    });
  };

  const navigateSection = (event) => {
    const link = event.target.closest('a[href^="#"]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const hash = link.getAttribute('href');
    const section = document.getElementById(hash.slice(1));
    if (!section) return;
    event.preventDefault();
    cancelScroll();
    setMenuOpen(false);
    const margin = parseFloat(getComputedStyle(section).scrollMarginTop) || 0;
    const top = window.scrollY + section.getBoundingClientRect().top - margin;
    const dayTop = dayRef.current && window.scrollY + dayRef.current.getBoundingClientRect().top;
    const skipRange = !staticDay && dayRef.current ? {
      start: dayTop - (headerRef.current?.offsetHeight || 0),
      end: dayTop + dayRef.current.offsetHeight - window.innerHeight,
    } : undefined;
    // Both ends of the pinned range render the same geometry. Hold the scene
    // while section navigation skips that travel, so the page never appears to pause.
    selectedMomentRef.current = moment;
    if (window.location.hash !== hash) window.history.pushState(window.history.state, '', hash);
    scrollTo(top, {
      skipRange,
      onCancel: resumeDay,
      onComplete: () => {
        resumeDay();
        const temporaryTabIndex = !section.hasAttribute('tabindex');
        if (temporaryTabIndex) section.setAttribute('tabindex', '-1');
        section.focus({ preventScroll: true });
        if (temporaryTabIndex) section.addEventListener('blur', () => section.removeAttribute('tabindex'), { once: true });
      },
    });
  };

  return (
    <div className="wellwired" id="wellwired-top" onClick={navigateSection}>
      <a className="ww-skip" href="#ww-main">
        Skip to content
      </a>
      <header
        ref={headerRef}
        className={`ww-header ww-wrap${isScrolled ? " ww-header-scrolled" : ""}`}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) setMenuOpen(false);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape" && menuOpen) {
            setMenuOpen(false);
            menuButtonRef.current?.focus();
          }
        }}
      >
        <div className="ww-header-brand">
          <a
            href="#wellwired-top"
            className="ww-lockup"
            aria-label="Wellwired home"
            onClick={() => setMenuOpen(false)}
          >
            <span className="ww-wordmark">Wellwired</span>
          </a>
          <span className="ww-header-endorsement">
            <span>by</span>
            <Link to="/" className="ww-parent-link" aria-label="Back to BAi Systems" title="Back to BAi Systems">
              <img src="/bai-wordmark-white.svg" alt="" width="40" height="17" />
            </Link>
          </span>
        </div>
        <button
          ref={menuButtonRef}
          className="ww-menu-toggle"
          type="button"
          aria-expanded={menuOpen}
          aria-controls="ww-navigation"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span className="ww-crossfade ww-menu-label">
            <span aria-hidden={menuOpen}>Menu</span>
            <span aria-hidden={!menuOpen}>Close</span>
          </span>
          <span className="ww-plus" aria-hidden="true" />
        </button>
        <nav
          id="ww-navigation"
          className={menuOpen ? "ww-navigation-open" : ""}
          aria-label="Wellwired navigation"
          onClick={(event) => {
            if (event.target.closest("a")) setMenuOpen(false);
          }}
        >
          <div className="ww-navigation-links">
            <a href="#day-complete">The pillars</a>
            <a href="#process">Design</a>
            <a href="#build">Build</a>
            <a href="#evidence">Verify</a>
            <a href="#begin" className="ww-nav-cta">
              Let’s talk <span aria-hidden="true">↗</span>
            </a>
          </div>
        </nav>
      </header>
      <main id="ww-main">
        <section className="ww-hero ww-wrap">
          <div className="ww-hero-copy">
            <p className="ww-kicker">
              Low-RF technology · Indoor air quality
            </p>
            <h1>
              Low-RF technology.
              <br />
              <span>Air quality built in.</span>
            </h1>
            <p className="ww-hero-description">
              Wellwired is the healthy-home design practice of BAi Systems. We
              design the controls and connections behind your new home’s air
              quality, low-RF connectivity, leak protection and lighting—with one
              coordinated plan for your architect and trades.
            </p>
            <a href="#begin" className="ww-action">
              Start with a free fit call <span aria-hidden="true">↗</span>
            </a>
            <p className="ww-micro">
              New construction. Designed remotely, wherever you build.
            </p>
          </div>
          <div
            className="ww-night-art"
            aria-label="Architectural concept illustration: an evening bedroom with two warm bedside lamps"
          >
            <span className="ww-time">22:00</span>
            <RoomStudy moment={4} hero className="ww-hero-room-study" />
            <div className="ww-night-caption">
              <span className="ww-fine-line" />
              <p>Selected Wi-Fi off.<br />Air and leak monitoring stay on.</p>
            </div>
            <span className="ww-art-label">
              Evening study · AI concept rendering
            </span>
          </div>
          <div className="ww-hero-bottom">
            <span>Hardwired by design. No screens required.</span>
            <a href="#day-complete">
              Explore the five pillars <span aria-hidden="true">↓</span>
            </a>
          </div>
        </section>
        <section id="day-complete" className="ww-pillars ww-wrap ww-section">
          <div id="approach" className="ww-section-heading">
            <p className="ww-kicker">What we design</p>
            <h2>Five pillars. One coordinated plan.</h2>
          </div>
          <div className="ww-pillar-gallery">
            {pillars.map(([name, title, summary, detail, label], i) => (
              <article className={`ww-pillar-feature ww-pillar-${name.toLowerCase()}`} key={name}>
                <div className="ww-pillar-picture">
                  <PillarStudy name={name} />
                  <div className="ww-pillar-label"><span className="ww-index">0{i + 1}</span><h3>{label || name}</h3></div>
                </div>
                <div className="ww-pillar-copy">
                  <h4>{title}</h4>
                  <p className="ww-pillar-summary">{summary}</p>
                  <WellwiredDisclosure className="ww-pillar-detail" title="The design detail" label={`The design detail: ${label || name}`}>
                    <p>{detail}</p>
                  </WellwiredDisclosure>
                </div>
              </article>
            ))}
          </div>
          <p className="ww-studies-caption">Five material studies. One home. <span>Concept illustrations</span></p>
        </section>
        <section
          id="through-the-day"
          className="ww-day-scroll"
          ref={dayRef}
          aria-label="The house through the day"
        >
          <div
            className={`ww-day ww-day-pinned ${moment >= 3 ? "ww-day-dark-text" : ""}`}
          >
            <div className="ww-day-backgrounds" aria-hidden="true">
              {moments.map((item, i) => (
                <div
                  key={item.name}
                  className={`ww-day-background ww-day-${item.name.toLowerCase()}`}
                  style={{
                    opacity: i <= moment ? 1 : 0,
                  }}
                />
              ))}
            </div>
            <div className="ww-wrap ww-day-frame">
              <div className="ww-day-top">
                <p className="ww-kicker">The house keeps time with you</p>
                <WellwiredClock minutes={moments[moment].minutes} />
              </div>
              <div className="ww-day-composition">
                <div className="ww-day-scenes">
                  {moments.map((item, i) => (
                    <div
                      key={item.name}
                      className={`ww-day-content ${i >= 3 ? "ww-day-dark-text" : ""}`}
                      aria-hidden={i !== moment}
                      style={{
                        opacity: i === moment ? 1 : 0,
                      }}
                    >
                      <h2>{item.title}</h2>
                      <p>{item.copy}</p>
                    </div>
                  ))}
                </div>
                <figure className="ww-day-visual">
                  <RoomStudy moment={moment} />
                  <figcaption><span>One room. A changing day.</span><span>AI concept rendering</span></figcaption>
                </figure>
              </div>
              <div
                className="ww-day-controls"
                role="group"
                aria-label="Explore the house through the day"
                style={{ '--ww-moment': moment }}
              >
                {moments.map((item, i) => (
                  <button
                    key={item.name}
                    aria-pressed={i === moment}
                    onClick={() => selectMoment(i)}
                  >
                    <span className="ww-time">{item.time}</span>
                    <span>{item.name}</span>
                    <span className="ww-day-choice-arrow" aria-hidden="true">→</span>
                  </button>
                ))}
                <span className="ww-day-marker" aria-hidden="true"><span className="ww-day-dot" /></span>
              </div>
              <div className="ww-day-hint">
                <span className="ww-crossfade">
                  <span aria-hidden={!staticDay}>Choose a time of day above</span>
                  <span aria-hidden={staticDay}>Scroll, or choose a time of day</span>
                </span>
                <a href="#phases">See how we bring it together ↓</a>
              </div>
            </div>
          </div>
        </section>
        <section id="phases" className="ww-path ww-wrap" aria-labelledby="ww-path-title">
          <div className="ww-section-heading">
            <p className="ww-kicker">One path, three phases</p>
            <h2 id="ww-path-title">From design to handover.</h2>
            <p>Each phase is priced separately. Most clients start with design.</p>
          </div>
          <div className="ww-phase-grid">
            <article className="ww-phase-design">
              <span className="ww-index">01</span>
              <h3>Design and engineering</h3>
              <p>Your priorities, translated into one coordinated Blueprint for every trade.</p>
              <a className="ww-text-link" href="#process">Begin with design <span aria-hidden="true">↗</span></a>
            </article>
            <article>
              <span className="ww-index">02</span>
              <h3>Build</h3>
              <p>Choose a BAi build or a partner who knows the selected system. BAi supplies the hardware either way.</p>
              <a className="ww-text-link" href="#build">Your two build options <span aria-hidden="true">↗</span></a>
            </article>
            <article>
              <span className="ww-index">03</span>
              <h3>Verify · optional</h3>
              <p>Add a written handover record of the measurements agreed for your home, including relevant day and night states.</p>
              <a className="ww-text-link" href="#evidence">See what you receive <span aria-hidden="true">↗</span></a>
            </article>
          </div>
        </section>
        <section id="process" className="ww-process ww-wrap ww-section">
          <div className="ww-process-intro">
            <div className="ww-section-heading">
              <p className="ww-kicker">01 · Design and engineering</p>
              <h2>
                First, a conversation.
                <br />
                Then, a buildable plan.
              </h2>
              <p>
                Start with your plans, your household and what matters most.
                The Consult sets priorities. The Blueprint gives your architect
                and trades one coordinated plan for the controls and connections.
              </p>
              <p>We coordinate with your architect, specialist designers and trades before the walls close. Bring your building biologist’s recommendations into the Consult.</p>
            </div>
            <figure className="ww-process-photo">
              <img
                src="/images/wellwired/bai-hallway.jpg"
                srcSet="/images/wellwired/bai-hallway-640.jpg 640w, /images/wellwired/bai-hallway.jpg 1365w"
                sizes="(max-width: 600px) 90vw, 50vw"
                width="1365"
                height="2048"
                loading="lazy"
                decoding="async"
                alt="Sunlight falls across the paneled walls and oak floor of a hallway with glass pendant lights."
              />
              <figcaption>BAi project photography · Toro Visuals</figcaption>
            </figure>
          </div>
          <div className="ww-steps">
            {[
              [
                "01",
                "Fit call",
                "A conversation to find the fit.",
                "15 minutes to discuss your project and whether Wellwired is right for it.",
                "Free",
              ],
              [
                "02",
                "Consult",
                "A direction, in writing.",
                "A virtual working session of about 90 minutes, followed by a written brief: five-pillar recommendations, priorities, and a scope and budget range for your Blueprint.",
                `${consultPrice} · credited in full toward Blueprint`,
              ],
              [
                "03",
                "Blueprint",
                "A plan your trades can build.",
                "The selected systems, hardware and coordinated drawings your team builds from.",
                `${blueprintRate} · quoted after your Consult`,
              ],
            ].map(([n, title, sub, copy, price]) => (
              <article key={n}>
                <span className="ww-index">{n}</span>
                <h3>{title}</h3>
                <h4>{sub}</h4>
                <p>{copy}</p>
                {n === "03" && <>
                  <ul className="ww-blueprint-deliverables">
                    <li>Selected hardware and bill of materials.</li>
                    <li>Device locations, riser diagrams and panel schedules.</li>
                    <li>Trade drawings, labelling and control sequences.</li>
                    <li>Commissioning plan and acceptance criteria.</li>
                  </ul>
                  <p className="ww-blueprint-allowance">A stated allowance covers questions during construction.</p>
                </>}
                <strong>{price}</strong>
              </article>
            ))}
          </div>
        </section>
        <section id="build" className="ww-build">
          <div className="ww-wrap ww-section">
            <div className="ww-section-heading">
              <p className="ww-kicker">02 · Build · quoted separately</p>
              <h2>One Blueprint.<br />Two ways to build it.</h2>
              <p>Choose the team that suits your project. BAi Systems supplies all hardware, kitted, labelled and itemized on the quote, for either route.</p>
            </div>
            <div className="ww-build-options">
              <article>
                <h3>A BAi build</h3>
                <p>BAi assembles and programs the panels. Local trades install to the drawings; BAi commissions the system and provides its ongoing support, including optional remote care.</p>
                <p className="ww-phase-price">BAi quotes the build and support scope.</p>
              </article>
              <article>
                <h3>A partner build</h3>
                <p>A partner who knows the selected system builds the panels, programs and commissions the home, and supports it after handover. The partner works to the same drawings and acceptance criteria.</p>
                <p className="ww-phase-price">Your partner quotes the build and support scope.</p>
              </article>
            </div>
            <p className="ww-boundary">Wellwired remains your design authority. Its Blueprint includes a stated allowance for construction questions; programming, commissioning and ongoing system support belong to the build team you choose.</p>
          </div>
        </section>
        <section id="evidence" className="ww-evidence ww-wrap ww-section">
          <div>
            <p className="ww-kicker">03 · Verify · optional add-on</p>
            <h2>
              More than a feeling.
              <br />A record you can read.
            </h2>
            <p>
              Add measurements at handover to check agreed design targets.
              Your air-quality and electrical priorities inform the scope. The Verify quote
              defines what will be measured, where and in which operating states,
              including day and night comparisons where relevant.
            </p>
            <p>
              Verify is optional and quoted separately. You receive a written
              report with the readings, instruments and method recorded, including
              relevant conditions outside the home’s control.
            </p>
            <a href="#begin" className="ww-text-link">
              Ask about Verify <span aria-hidden="true">↗</span>
            </a>
          </div>
          <div className="ww-report" aria-label="Illustrative Verify report structure, with no project measurements">
            <div className="ww-report-top">
              <span className="ww-wordmark">Wellwired</span>
              <span>by BAi</span>
            </div>
            <h3>Verify</h3>
            <p className="ww-report-sub">
              Project measurement record
            </p>
            <dl>
              {[
                ["Project", "Locations and agreed targets"],
                ["Air scope", "Indicators defined in the quote"],
                ["Electrical scope", "Sources and states defined in the quote"],
                ["Conditions", "Operating state, date and time"],
                ["Method", "Instruments and procedures"],
                ["Findings", "Readings and follow-up"],
              ].map(([a, b]) => (
                <div key={a}>
                  <dt>{a}</dt>
                  <dd>{b}</dd>
                </div>
              ))}
            </dl>
            <p className="ww-report-footer">
              Illustrative report structure.
              <br />
              No project measurements shown.
            </p>
          </div>
        </section>
        <section className="ww-faq ww-wrap ww-section">
          <p className="ww-kicker">A few practical details</p>
          <div>
            {[
              [
                "Can you work with our architect, builder or building biologist?",
                "Yes. Bring your team and their recommendations into the Consult. We coordinate the agreed technology priorities with your architect, building biologist and relevant specialists, translating them into controls, connections and drawings the trades can build.",
              ],
              [
                "Is low RF the same as low EMF?",
                "RF describes radio-frequency signals, including Wi-Fi. Broader low-EMF planning also considers electric and magnetic fields from wiring and equipment. Hardwiring and Wi-Fi schedules alone do not establish the field levels of a finished home; outside sources and personal devices also matter. We define the design priorities and any broader assessment scope with you early.",
              ],
              [
                "Does air monitoring tell us everything about the air?",
                "Sensors measure their specified indicators. CO₂ helps inform ventilation, while particles, VOC trends and humidity add other information. Source control, ventilation and filtration each matter. We coordinate monitoring and controls with the mechanical plan and discuss additional specialist testing separately.",
              ],
              [
                "Does automatic shutoff prevent every kind of water damage?",
                "Leak sensing and shutoff help limit further water release from detected plumbing leaks. Sensor coverage, valve locations and response settings are part of the design. Roof leaks, condensation and other moisture sources need their own measures. Detected leaks still need repair, and wet materials need prompt drying.",
              ],
              [
                "Does the house need apps or internet to work?",
                "The house runs locally and works without internet. Screens and phone controls are optional; everyday use does not require them. Remote care needs an internet connection.",
              ],
              [
                "Are Build and Verify included in the Blueprint?",
                "Design, Build and Verify are priced separately. Choose a BAi build or a partner build; BAi supplies the selected hardware either way. Verify is optional and is not included with the Blueprint.",
              ],
              [
                "Who selects the hardware and supports the home?",
                "Wellwired selects the hardware in the Blueprint; BAi Systems supplies it, itemized on the quote, for either build route. BAi supports a BAi build; your partner supports a partner build. Wellwired provides design, its stated construction-question allowance and the report if you add Verify.",
              ],
              [
                "What stays private?",
                "The house works locally. Nothing about the people in it leaves unless you turn on remote care. Opting in shares operational data, including history recorded since installation, with your name and address removed. Turning it off stops sharing. If you add Verify, you separately agree to share the handover measurements used in your report.",
              ],
              [
                "Can you help with an existing home?",
                "Wellwired currently focuses on new construction. The systems are designed together before the walls close.",
              ],
            ].map(([q, a]) => (
              <WellwiredDisclosure key={q} title={q}>
                <p>{a}</p>
              </WellwiredDisclosure>
            ))}
          </div>
        </section>
        <section id="begin" className="ww-begin">
          <div className="ww-wrap ww-section ww-begin-layout">
            <div>
              <p className="ww-kicker">Begin before the walls close.</p>
              <h2>Let’s make room<br />for a quieter home.</h2>
              <p>Tell us where you are in the plans.<br />We’ll start with a free, 15-minute fit call.</p>
              {fitCallUrl && <a href={fitCallUrl} className="ww-action" target="_blank" rel="noopener noreferrer">Book a free fit call <span aria-hidden="true">↗</span></a>}
              <p>Architect, designer or building biologist? Choose a design-team conversation in the inquiry form.</p>
              <a className="ww-text-link" href="mailto:hello@bai-systems.com">hello@bai-systems.com</a>
            </div>
            <WellwiredInquiry includeBuildingBiologist />
          </div>
        </section>
      </main>
      <footer className="ww-footer ww-wrap">
        <div>
          <a className="ww-lockup" href="#wellwired-top">
            <span className="ww-wordmark">Wellwired</span>
            <span className="ww-endorsement">by BAi</span>
          </a>
          <p>All the intelligence. None of the noise.</p>
        </div>
        <Link to="/" aria-label="BAi Systems home">
          <img src="/bai-lockup-white.svg" alt="BAi Systems" />
          <span>
            BAi Systems <span aria-hidden="true">↗</span>
          </span>
        </Link>
        <div className="ww-footer-bottom">
          <span>New construction. Design, Build and optional Verify priced separately.</span>
        </div>
      </footer>
    </div>
  );
}
